import React, { useState } from "react";
import UserAvatar from "../Avatar/UserAvatar";
import BlogCommentForm from "./BlogCommentForm";

export default function BlogComment({ comments = [], blogId, onReplySubmitted }) {
  const [replyingTo, setReplyingTo] = useState(null);

  if (!comments || comments.length === 0) {
    return (
      <div className="mt-[60px]">
        <h1 className="text-[30px] tracking-tight font-semibold text-white pb-10">
          Comments (0)
        </h1>
        <p className="text-gray-400">No comments yet. Be the first to comment!</p>
      </div>
    );
  }

  // Calculate total comments including replies for the count
  const totalComments = comments.reduce((acc, comment) => {
    return acc + 1 + (comment.replies ? comment.replies.length : 0);
  }, 0);

  return (
    <div className="mt-[60px]">
      <h1 className="text-[30px] tracking-tight font-semibold text-white pb-10">
        Comments ({totalComments > 9 ? totalComments : `0${totalComments}`})
      </h1>

      {comments.map((comment) => (
        <div key={comment.id} className="mb-[53px]">
          {/* Parent Comment */}
          <div className="flex flex-col md:flex-row gap-10 md:mr-6">
            <UserAvatar className="w-16 h-16 md:w-20 md:h-20 shrink-0" />
            <div className="flex-1">
              <div className="flex flex-col items-center justify-center sm:flex-row">
                <h1 className="flex-1 font-semibold text-18 text-white">
                  {comment.author_name || "Anonymous User"}
                </h1>
                <p className="font-medium text-paragraph">{comment.date}</p>
              </div>
              <p className="text-paragraph mb-1.5">
                {comment.content}
              </p>

              {/* Only show Reply button for top-level comments (enforcing depth 1) */}
              {!comment.parent_id && (
                <button
                  type="button"
                  className="cursor-pointer"
                  onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                >
                  <div className="flex items-center gap-2 group">
                    <p className="mb-[1px] font-medium text-paragraph leading-5 font-inter border-b border-transparent before:block before:pb-[1px] before:border-purple before:font-medium before:text-purple before:leading-5 before:font-inter before:border-b before:content-['Reply'] before:absolute before:-bottom-[1px] before:transition-all before:duration-300 before:w-0 hover:before:w-full before:overflow-hidden before:h-[21px] relative">
                      Reply
                    </p>
                    <svg
                      width="7"
                      height="11"
                      viewBox="0 0 7 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        className="transition-all duration-300 delay-300 group-hover:stroke-purple group-hover:delay-0"
                        d="M1.5 10L5.29289 6.20711C5.62623 5.87377 5.79289 5.70711 5.79289 5.5C5.79289 5.29289 5.62623 5.12623 5.29289 4.79289L1.5 1"
                        stroke="#6D6D6D"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Reply Form */}
          {replyingTo === comment.id && (
            <div className="mt-8 ml-0 md:ml-28">
              <BlogCommentForm
                blogId={blogId}
                parentId={comment.id}
                onCommentSubmitted={(data) => {
                  setReplyingTo(null);
                  if (onReplySubmitted) onReplySubmitted(data);
                }}
                onCancel={() => setReplyingTo(null)}
              />
            </div>
          )}

          {/* Nested Replies */}
          {comment.replies && comment.replies.length > 0 && (
            <div className="mt-8">
              {comment.replies.map((reply) => (
                <div key={reply.id} className="flex flex-col md:flex-row gap-10 md:ml-28 mb-8 border-l-2 border-purple/20 pl-6">
                  <UserAvatar className="w-12 h-12 md:w-16 md:h-16 shrink-0" />
                  <div className="flex-1">
                    <div className="flex flex-col items-center justify-center sm:flex-row">
                      <h1 className="flex-1 font-semibold text-16 text-white">
                        {reply.author_name || "Anonymous User"}
                      </h1>
                      <p className="font-medium text-paragraph text-sm">{reply.date}</p>
                    </div>
                    <p className="text-paragraph mb-1.5 text-sm">
                      {reply.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
